import { useState, useEffect, useCallback, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { message } from "antd";
import { NotificationSound } from "../../../containers/Topbar/NotificationTopBar/NotificationSound";
import { getApi } from "../../Axios_Dev";
import { baseUrl2 } from "../../Api";

export const useSignalRNotifications = (getLogout) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef(null);
  const hasInitialLoad = useRef(false);

  const fetchNotifications = useCallback(() => {
    if (hasInitialLoad.current) return;

    getApi(
      `Notification/GetNotifications`,
      (data) => {
        if (data && Array.isArray(data)) {
          const formattedNotifications = data.map((item) => ({
            id: item.Id,
            title: item.Title,
            message: item.Description,
            time: formatTime(item.CreatedDate),
            type: getNotificationType(item.Title),
            unread: true,
          }));

          setNotifications(formattedNotifications);
          setUnreadCount(formattedNotifications.length);
          hasInitialLoad.current = true;
        }
      },
      "data",
      getLogout,
      false,
    );
  }, [getLogout]);

  const getNotificationType = (title) => {
    if (!title) return "info";
    const titleLower = title.toLowerCase();
    if (titleLower.includes("alert") || titleLower.includes("stock"))
      return "warning";
    if (titleLower.includes("success") || titleLower.includes("completed"))
      return "success";
    if (titleLower.includes("error") || titleLower.includes("failed"))
      return "error";
    return "info";
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Just now";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      if (diffInSeconds < 60) return "Just now";
      if (diffInSeconds < 3600)
        return `${Math.floor(diffInSeconds / 60)} mins ago`;
      if (diffInSeconds < 86400)
        return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      if (diffInSeconds < 604800)
        return `${Math.floor(diffInSeconds / 86400)} days ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return "Just now";
    }
  };

  const handleNewNotification = useCallback((notification) => {
    console.log("🔔 Raw notification from backend:", notification);

    const formattedNotification = {
      id: notification.Id || notification.id || Date.now(),
      title: notification.Title || notification.title || "New Notification",
      message:
        notification.Description ||
        notification.description ||
        notification.Message ||
        notification.message ||
        "No description",
      time: "Just now",
      type: getNotificationType(notification.Title || notification.title),
      unread: true,
    };

    console.log("📬 Formatted notification:", formattedNotification);

    setNotifications((prev) => [formattedNotification, ...prev]);
    setUnreadCount((prev) => prev + 1);

    try {
      NotificationSound();
    } catch (error) {
      console.error("Notification sound error:", error);
    }

    message.info({
      content: (
        <div style={{ padding: "5px 0" }}>
          <strong style={{ fontSize: "14px", color: "#1890ff" }}>
            {formattedNotification.title}
          </strong>
          <br />
          <span style={{ fontSize: "13px", color: "#666" }}>
            {formattedNotification.message}
          </span>
        </div>
      ),
      duration: 6,
      style: {
        marginTop: "50px",
      },
    });

    console.log("✅ Notification processed successfully");
  }, []);

  const setupSignalR = useCallback(() => {
    if (connectionRef.current) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    let hubUrl = baseUrl2.endsWith("/") ? baseUrl2.slice(0, -1) : baseUrl2;
    hubUrl = `${hubUrl}/hubs/notifications`;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Error)
      .build();

    connection.onclose(() => setIsConnected(false));
    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(() => setIsConnected(true));

    connection.on("ReceiveNotification", handleNewNotification);

    connection
      .start()
      .then(() => {
        setIsConnected(true);
        connectionRef.current = connection;
      })
      .catch(() => {
        setIsConnected(false);
        connectionRef.current = null;
      });

    connectionRef.current = connection;
  }, [handleNewNotification]);

  useEffect(() => {
    fetchNotifications();
    setupSignalR();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
      }
    };
  }, [fetchNotifications, setupSignalR]);

  return {
    notifications,
    unreadCount,
    isConnected,
  };
};
